import { useContext } from '@/hooks/useContext'
import {
  CommunitiesRoutes,
  NetworksRoutes,
  PoolingRoutes,
} from '@/routes/_hooks/routes.enum'
import { generatePath, useLocation, useParams } from '@/routing'

type ContextualPathKey =
  | 'materials'
  | 'addFromTemplates'
  | 'dashboard'
  | 'bookingsAsOwner'
  | 'bookingsRequests'
  | 'bookingShow'
  | 'networks'
  | 'materialEditStep'
  | 'materialShow'
  | 'materialCalendar'
  | 'materialLogBook'

type GenerateContextualPathParams = {
  uuid?: string
  id?: string
  materialSlug?: string
  step?: string
}

const getSlugFromIri = (contextIri: string | null): string | null => {
  if (!contextIri) return null
  const match = contextIri.match(/\/(?:circles|networks)\/([^/]+)/)
  return match?.[1] ?? null
}

export const useContextualPath = () => {
  const { isCircle, isNetwork, contextIri } = useContext()
  const location = useLocation()
  const params = useParams<{
    slug?: string
    communitySlug?: string
    networkSlug?: string
  }>()
  const contextSlugFromStorage = getSlugFromIri(contextIri)
  const isCircleRoute = location.pathname.startsWith('/c/')
  const isNetworkRoute = location.pathname.startsWith('/n/')
  // Only treat `params.slug` as community/network slug on `c/:slug` or `n/:slug` trees.
  // On `/pooling/:slug/edit`, `slug` is the material slug — using it here produced wrong
  // `/c/<materialSlug>/materials/...` URLs and loader redirects to `/communities/<materialSlug>`.
  const slugFromRoute =
    params.communitySlug ??
    params.networkSlug ??
    (isCircleRoute || isNetworkRoute ? params.slug : undefined) ??
    null

  // Prefer URL slug when stable; active context slug wins when URL and context disagree mid-switch.
  const contextSlug = slugFromRoute ?? contextSlugFromStorage

  const generateContextualPath = (
    key: ContextualPathKey,
    params?: GenerateContextualPathParams
  ): string => {
    switch (key) {
      case 'materials':
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.Materials, {
            slug: contextSlugFromStorage,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.Materials, {
            slug: contextSlugFromStorage,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.Materials, {
            slug: contextSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.Materials, { slug: contextSlug })
        }
        return PoolingRoutes.MyMaterials
      case 'addFromTemplates':
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.AddFromTemplates, {
            slug: contextSlugFromStorage,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.AddFromTemplates, {
            slug: contextSlugFromStorage,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.AddFromTemplates, {
            slug: contextSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.AddFromTemplates, {
            slug: contextSlug,
          })
        }
        return PoolingRoutes.AddFromTemplates
      case 'dashboard':
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.Dashboard, {
            slug: contextSlugFromStorage,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.Dashboard, {
            slug: contextSlugFromStorage,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.Dashboard, {
            slug: contextSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.Dashboard, { slug: contextSlug })
        }
        return '/'
      case 'bookingsAsOwner':
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.BookingAsOwner, {
            slug: contextSlugFromStorage,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.BookingAsOwner, {
            slug: contextSlugFromStorage,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.BookingAsOwner, {
            slug: contextSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.BookingAsOwner, {
            slug: contextSlug,
          })
        }
        return PoolingRoutes.MyBookingAsOwner
      case 'bookingsRequests':
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.BookingRequests, {
            slug: contextSlugFromStorage,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.BookingRequests, {
            slug: contextSlugFromStorage,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.BookingRequests, {
            slug: contextSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.BookingRequests, {
            slug: contextSlug,
          })
        }
        return PoolingRoutes.MyBookingRequests
      case 'bookingShow': {
        const bookingUuid = params?.uuid ?? params?.id
        if (!bookingUuid) {
          throw new Error('bookingShow requires "uuid" or "id"')
        }
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.BookingShow, {
            slug: contextSlugFromStorage,
            uuid: bookingUuid,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.BookingShow, {
            slug: contextSlugFromStorage,
            uuid: bookingUuid,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.BookingShow, {
            slug: contextSlug,
            uuid: bookingUuid,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.BookingShow, {
            slug: contextSlug,
            uuid: bookingUuid,
          })
        }
        return generatePath(PoolingRoutes.MyBookingShow, { id: bookingUuid })
      }
      case 'networks':
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.Networks, {
            slug: contextSlugFromStorage,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.Networks, { slug: contextSlug })
        }
        return NetworksRoutes.MyNetworks
      case 'materialEditStep': {
        const materialSlug = params?.materialSlug
        const step = params?.step
        if (!materialSlug || !step) {
          throw new Error('materialEditStep requires "materialSlug" and "step"')
        }
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.MaterialEditStep, {
            communitySlug: contextSlugFromStorage,
            materialSlug,
            step,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.MaterialEditStep, {
            networkSlug: contextSlugFromStorage,
            materialSlug,
            step,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.MaterialEditStep, {
            communitySlug: contextSlug,
            materialSlug,
            step,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.MaterialEditStep, {
            networkSlug: contextSlug,
            materialSlug,
            step,
          })
        }
        return generatePath(PoolingRoutes.EditMyMaterialStep, {
          slug: materialSlug,
          step,
        })
      }
      case 'materialShow': {
        const materialSlug = params?.materialSlug
        if (!materialSlug) {
          throw new Error('materialShow requires "materialSlug"')
        }
        return generatePath(PoolingRoutes.Show, { slug: materialSlug })
      }
      case 'materialCalendar': {
        const materialSlug = params?.materialSlug
        if (!materialSlug) {
          throw new Error('materialCalendar requires "materialSlug"')
        }
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.MaterialCalendar, {
            slug: contextSlugFromStorage,
            materialSlug,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.MaterialCalendar, {
            slug: contextSlugFromStorage,
            materialSlug,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.MaterialCalendar, {
            slug: contextSlug,
            materialSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.MaterialCalendar, {
            slug: contextSlug,
            materialSlug,
          })
        }
        return generatePath(PoolingRoutes.Calendar, { slug: materialSlug })
      }
      case 'materialLogBook': {
        const materialSlug = params?.materialSlug
        if (!materialSlug) {
          throw new Error('materialLogBook requires "materialSlug"')
        }
        if (isCircle && contextSlugFromStorage) {
          return generatePath(CommunitiesRoutes.MaterialMaintenanceLogBook, {
            slug: contextSlugFromStorage,
            materialSlug,
          })
        }
        if (isNetwork && contextSlugFromStorage) {
          return generatePath(NetworksRoutes.MaterialMaintenanceLogBook, {
            slug: contextSlugFromStorage,
            materialSlug,
          })
        }
        if ((isCircleRoute || isCircle) && contextSlug) {
          return generatePath(CommunitiesRoutes.MaterialMaintenanceLogBook, {
            slug: contextSlug,
            materialSlug,
          })
        }
        if ((isNetworkRoute || isNetwork) && contextSlug) {
          return generatePath(NetworksRoutes.MaterialMaintenanceLogBook, {
            slug: contextSlug,
            materialSlug,
          })
        }
        return generatePath(PoolingRoutes.MaterialMaintenanceLogBook, {
          slug: materialSlug,
        })
      }
      default:
        return '/'
    }
  }

  return { generateContextualPath }
}
